import { PlusIcon } from '@heroicons/react/16/solid';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  FirmTaskTemplateSchema,
  useGetFirmTaskTemplatesQuery,
} from '@/services/api/firmTaskTemplatesService';

import { TaskTemplateCard } from './TaskTemplateCard';
import { TaskTemplateEditorDialog } from './TaskTemplateEditorDialog';

interface TaskTemplatesSectionProps {
  firmId: string;
}

export const TaskTemplatesSection = ({ firmId }: TaskTemplatesSectionProps) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<FirmTaskTemplateSchema | null>(null);

  const { data: templates, isLoading } = useGetFirmTaskTemplatesQuery(firmId);

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setEditorOpen(true);
  };

  const handleEdit = (template: FirmTaskTemplateSchema) => {
    setEditingTemplate(template);
    setEditorOpen(true);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditingTemplate(null);
  };

  // Sort templates by created date (most recent first)
  const sortedTemplates = useMemo(() => {
    return [...(templates ?? [])].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [templates]);

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">
            Firm Task Templates
          </h2>
          <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed max-w-xl">
            Define custom tasks specific to your firm. Enabled templates
            auto-create on every new case. Disabled templates remain available
            for one-off manual assignment to individual cases.
          </p>
        </div>
        {/* Only show header button when templates exist (empty state has its own CTA) */}
        {!isLoading && sortedTemplates.length > 0 && (
          <Button
            onClick={handleCreateNew}
            size="sm"
            variant="outline"
            className="flex-shrink-0"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Template
          </Button>
        )}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-1.5">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && sortedTemplates.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-zinc-200 bg-zinc-50/50 py-10 dark:border-zinc-800 dark:bg-zinc-900/50">
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-1">
            No task templates yet
          </p>
          <p className="text-xs text-zinc-400 dark:text-zinc-500 mb-4">
            Create templates for recurring firm-specific tasks
          </p>
          <Button onClick={handleCreateNew} variant="outline" size="sm">
            <PlusIcon className="h-4 w-4 mr-1" />
            Create your first template
          </Button>
        </div>
      )}

      {/* Templates list */}
      {!isLoading && sortedTemplates.length > 0 && (
        <div className="rounded-lg border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 divide-y divide-zinc-100 dark:divide-zinc-800">
          {sortedTemplates.map((template) => (
            <TaskTemplateCard
              key={template.id}
              firmId={firmId}
              template={template}
              onEdit={handleEdit}
            />
          ))}
        </div>
      )}

      <TaskTemplateEditorDialog
        firmId={firmId}
        open={editorOpen}
        onOpenChange={handleEditorClose}
        template={editingTemplate}
      />
    </div>
  );
};
